import { useMemo, useState, type FormEvent } from 'react'
import { t } from '../i18n'
import { formatPoints } from './marketplace-model'
import {
  initialFor,
  matchCustomers,
  type SearchField,
} from './customer-search-model'
import { FIXTURE_CUSTOMERS } from './customer-search-fixtures'

const FIELDS: { id: SearchField; label: string; placeholder: string }[] = [
  { id: 'phone', label: 'Téléphone', placeholder: 'searchPhonePlaceholder' },
  { id: 'name', label: 'Nom', placeholder: 'searchNamePlaceholder' },
  { id: 'email', label: 'E-mail', placeholder: 'searchEmailPlaceholder' },
]

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '+', '0', '⌫'] as const

/** "Rechercher un client" — the manual-lookup panel beside the scanner (Figma 2464:2501).
 *
 * The reason it exists is not convenience: four of the merchants interviewed asked for it
 * unprompted (PRD 1/§5bis #6). A customer with a flat battery, no phone on them, or a camera that
 * will not focus still has to be served during a rush, and today the cashier just gives up.
 *
 * Tapping a name identifies that customer immediately — same entry point as a successful scan.
 * That also makes the whole flow walkable with no camera and no backend, which is what a PM needs
 * to review it.
 *
 * ponytail: the list is mocked and filtered client-side. The real thing is TechSpec B6, and it is
 * NOT a smaller version of this — PRD §6 requires prefill only for prior-relationship customers,
 * a complete identifier for anyone else, masked identifiers in results, audit logging and rate
 * limiting. Do not promote this matching to production.
 */
export function CustomerSearchPanel({
  onPickCustomer,
}: {
  onPickCustomer: (consumerId: string) => void
}) {
  const [field, setField] = useState<SearchField>('phone')
  const [query, setQuery] = useState('')

  const results = useMemo(() => matchCustomers(FIXTURE_CUSTOMERS, query, field), [query, field])
  const active = FIELDS.find((f) => f.id === field)!

  function switchField(next: SearchField) {
    if (next === field) return
    // Clear on switch: a phone fragment is meaningless as a name query and would show an empty
    // list that looks like "this customer doesn't exist".
    setField(next)
    setQuery('')
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    // Submitting a lone exact match is the keyboard path; otherwise the cashier taps a row.
    // A raw value that matches nothing is still passed through, so a real consumer uid typed
    // during testing reaches the backend instead of being swallowed by the demo filter.
    const value = query.trim()
    if (!value) return
    onPickCustomer(results.length === 1 ? results[0].consumer_id : value)
  }

  return (
    <aside className="flex h-full w-[414px] shrink-0 flex-col overflow-hidden bg-white">
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-[38px] pt-[calc(var(--ls-safe-top)+28px)] pb-5">
        <h2 className="text-[17px] text-ink">{t('searchCustomer')}</h2>

        <div role="tablist" aria-label={t('searchCustomer')} className="flex gap-3">
          {FIELDS.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={f.id === field}
              onClick={() => switchField(f.id)}
              className={`rounded-full px-6 py-2.5 text-[16px] font-medium transition ${
                f.id === field ? 'bg-brand text-white' : 'bg-canvas text-ink-soft hover:bg-line'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <form onSubmit={submit}>
          <div className="flex items-center gap-3 rounded-lg border border-line px-4 py-3.5">
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
              className="size-[19px] shrink-0 text-ink-muted"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              // `text` even for phone: `type=tel` summons the iPad's own keyboard over the panel,
              // and the on-screen keypad below is the intended input.
              type="text"
              inputMode={field === 'phone' ? 'numeric' : 'text'}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label={t(active.placeholder as 'searchPhonePlaceholder')}
              placeholder={t(active.placeholder as 'searchPhonePlaceholder')}
              className="w-full bg-transparent text-[17px] text-ink outline-none placeholder:text-ink-muted"
            />
          </div>
        </form>

        <p className="text-[15px] text-ink-soft">{query.trim() ? t('results') : t('recents')}</p>

        <div className="flex flex-col gap-3">
          {results.map((c) => (
            <button
              key={c.consumer_id}
              type="button"
              onClick={() => onPickCustomer(c.consumer_id)}
              className="flex items-center gap-3.5 rounded-lg border border-line px-4 py-3 text-left transition hover:border-brand hover:bg-brand-50"
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[15px] font-semibold text-brand">
                {initialFor(c)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[16px] font-semibold text-ink">
                  {c.display_name}
                </span>
                {/* Falls back to whichever identifier the customer actually has — a real consumer
                  * may have no phone on file, and an empty subtitle reads like a broken row. */}
                <span className="block truncate text-[15px] text-ink-soft">
                  {(field === 'email' ? c.email : c.phone) ?? c.phone ?? c.email ?? ''}
                </span>
              </span>
              <span className="shrink-0 text-[16px] font-semibold text-brand">
                {formatPoints(c.points_balance)} {t('pts')}
              </span>
            </button>
          ))}
          {results.length === 0 && (
            <p className="py-3 text-[15px] text-ink-muted">{t('noCustomerFound')}</p>
          )}
        </div>
      </div>

      {/* Keypad + Historique are pinned: the results list scrolls, but the cashier's input never
        * scrolls out from under their thumb mid-rush. */}
      <div className="shrink-0 px-[38px] pb-6 pt-2">
        {field === 'phone' && (
          <div className="grid grid-cols-3 gap-3">
            {KEYS.map((k) => (
              <button
                key={k}
                type="button"
                aria-label={k === '⌫' ? t('backspace') : k}
                onClick={() =>
                  setQuery((q) => (k === '⌫' ? q.slice(0, -1) : q + k))
                }
                className="h-[50px] rounded-lg border border-line bg-white text-[19px] font-medium text-ink transition hover:bg-canvas active:scale-[0.97]"
              >
                {k}
              </button>
            ))}
          </div>
        )}

      </div>
    </aside>
  )
}
